/* eslint-disable no-nested-ternary */
import React, { useMemo } from "react";
import styles from "./DistanceBy.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import LocationIcon from "@Icons/LocationIcon";
import useIsDesktop from "hooks/useIsDesktop";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import sortModesByDuration from "@Utils/getConvertedMinutes";
import config from "@Config/index";
import { milesToKilometersConverter } from "@Pages/PropertyPage/components/PropertyDetailsSection/helper";
import DriveDark from "@Icons/DriveDark";
import TrainDark from "@Icons/TrainDark";
import WalkingDark from "@Icons/WalkingDark";
import LocationDark from "@Icons/LocationDark";
import { CANNIBALIZATION_ROLLOUT } from "constants/configConstants";

export const timeSimpleFormat = (
  input?: string | null,
  minLabel = "m",
  hourLabel = "h",
): string => {
  if (!input) return "N/A"; // Handle invalid inputs
  return input.replace(/\b hours?\b/g, hourLabel).replace(/\b mins?\b/g, minLabel);
};

// Define props interface
interface TransportData {
  DRIVING?: { duration?: string; distance?: string };
  TRANSIT?: { duration?: string; distance?: string };
  WALKING?: { duration?: string; distance?: string };
}

interface Props {
  data: TransportData | null;
  isPropertyPage?: boolean;
  regionType?: string;
  region?: { name: string; location?: { country?: { short_name?: string } } } | null;
  isGridView?: boolean;
  addTuncateForDistance?: boolean;
  customLocation?: Record<any, any>;
  isAgentView?: boolean;
}

const DistanceBy: React.FC<Props> = ({
  data,
  isPropertyPage = false,
  regionType,
  region,
  isGridView,
  addTuncateForDistance,
  customLocation,
  isAgentView,
}) => {
  const isDesktop = useIsDesktop() && !isGridView;
  const { t } = useTranslationHook();
  const universityText = t("University");
  const cityCenterText = t("searchpage.searchlist.searchlistitem.citycenter");
  const fromText = t("searchpage.searchlist.searchlistitem.from");

  const sortedTransportModes = useMemo(() => {
    if (!data) return [];
    return sortModesByDuration(data) || [];
  }, [data]);

  const transportComponents: any = useMemo(() => {
    if (!data) return {};
    const minLabel = t("minutes.short");
    const hourLabel = t("hours.short");
    return {
      DRIVING: data.DRIVING?.duration && (
        <span data-testid="searchpage-inventoryCard-drivingDistance" className={styles.walking}>
          <DriveDark /> <span>{timeSimpleFormat(data.DRIVING.duration, minLabel, hourLabel)}</span>
        </span>
      ),
      TRANSIT: data.TRANSIT?.duration && (
        <span data-testid="searchpage-inventoryCard-trainDistance" className={styles.walking}>
          <TrainDark /> <span>{timeSimpleFormat(data.TRANSIT.duration, minLabel, hourLabel)}</span>
        </span>
      ),
      WALKING: data.WALKING?.duration && (
        <span data-testid="searchpage-inventoryCard-walkingDistance" className={styles.walking}>
          <WalkingDark />{" "}
          <span>{timeSimpleFormat(data.WALKING.duration, minLabel, hourLabel)}</span>
        </span>
      ),
    };
  }, [data]);

  // Handle undefined data gracefully
  if (!data || sortedTransportModes.length === 0) {
    return <></>;
  }

  const getRegionText = (): string => {
    // Show university name for establishment type
    const isAU = CANNIBALIZATION_ROLLOUT.includes(region?.location?.country?.short_name || "");
    if (regionType === "establishment") {
      return isAU ? region?.name || universityText : universityText;
    }
    if (regionType === "sublocality" && region?.name) return region.name;
    if (customLocation && customLocation?.primary) return customLocation?.primary;
    return cityCenterText;
  };

  const renderDistanceFromRegion = () => {
    if (addTuncateForDistance) {
      return (
        <div className={styles.distanceWrap}>
          <LocationDark />
          <span
            className={styles.distanceOnMap}
            data-testid="searchpage-inventoryCard-cityCenterDistance"
          >
            <span className={styles.distanceCombinedText}>{`${
              config.IS_CHINA_APP
                ? milesToKilometersConverter(data?.WALKING?.distance)
                : data?.WALKING?.distance ?? "N/A"
            } ${fromText} ${getRegionText()}`}</span>
          </span>
        </div>
      );
    }
    return (
      <div>
        <span
          className={styles.distanceOnMap}
          data-testid="searchpage-inventoryCard-cityCenterDistance"
        >
          <LocationIcon />
          <span className={styles.distanceText}>{`${
            data?.WALKING?.distance ?? "N/A"
          } ${fromText}`}</span>
          <span className={styles.locationText}>{` ${getRegionText()}`}</span>
        </span>
      </div>
    );
  };

  return (
    <div
      className={formatClassNames(
        !isDesktop ? styles.container : styles.desktopContainer,
        isPropertyPage && styles.propertyWrap,
        isGridView && styles.gridView,
        isAgentView && styles.isAgentView,
      )}
      data-testid="searchpage-inventoryCard-distance"
    >
      {isDesktop && !isPropertyPage && renderDistanceFromRegion()}
      <span className={styles.duration}>
        (
        {sortedTransportModes.map((mode, index) => (
          <React.Fragment key={index}>
            {transportComponents[mode]}
            {index < sortedTransportModes.length - 1 && <span className={styles.dotSpan}>.</span>}
          </React.Fragment>
        ))}
        )
      </span>
    </div>
  );
};

export default wrapperHOC(DistanceBy, {
  componentName: "DistanceBy-SearchListItemDesktop-SearchPage",
  showForChina: true,
});
